import { useRipple } from "@chaocore/ripple";
import CookieType from "@constants/CookieType";
import CountriesIso2 from "@data/iso2-countries.json";
import LandingPricing from "@data/landing-pricing.json";
import Regions from "@data/regions.json";
import CountriesTimeZone from "@data/regions-to-countries.json";
import type CoverageItem from "@interfaces/CoverageItem";
import { type default as LandingPricingType } from "@interfaces/LandingPricing";
import type NetworkCarrier from "@interfaces/NetworkCarrier";
import type Product from "@interfaces/Product";
import type Region from "@interfaces/Region";
import tailwindConfig from "@root/tailwind.config";
import { useAccountDetailStore } from "@stores/account_detail";
import { useAuthenticationStore } from "@stores/authentication";
import { useConfigurationStore } from "@stores/configuration";
import { usePaymentStore } from "@stores/payment";
import axios from "@utilities/axios";
import { type ClassValue, clsx } from "clsx";
import { type TFunction } from "i18next";
import Cookies from "js-cookie";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import resolveConfig from "tailwindcss/resolveConfig";

export interface RegionItem {
  link: string;
  name: string;
}

export const cn = (...inputs: ClassValue[]): string => {
  return twMerge(clsx(inputs));
};

export const isDebugMode = (): boolean => process.env.GATSBY_DEBUG === "true";

export const isProductionMode = (): boolean =>
  typeof window !== "undefined" &&
  !!window.location?.hostname &&
  !!process.env.GATSBY_SITE_URL?.includes(window.location.hostname) &&
  window.location.hostname !== "localhost";

export const defaultRipple = useRipple({});

export const primaryRipple = useRipple({
  color: resolveConfig(tailwindConfig).theme.colors.primary.DEFAULT,
});

export const getDurationText = (
  t: TFunction<"translation", undefined, "translation">,
  product: Product,
): string => {
  let durationText = "";
  switch (parseInt(product.duration.toString())) {
    case -1:
      durationText = t("no_expiration");
      break;
    case 7:
    case 30:
      if (product.has_subscription) {
        durationText = t("monthly");
      } else {
        durationText = product.duration + " " + t("days");
      }
      break;
  }
  return durationText;
};

export const handleRedirectToApp = (): void => {
  if (typeof window !== "undefined" && window.location.href) {
    const appUrl = process.env.GATSBY_APP_URL ?? "";

    let fallbackUrl;
    if (/android/i.test(navigator.userAgent)) {
      fallbackUrl = process.env.GATSBY_PLAY_STORE_LINK ?? "/";
    } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      fallbackUrl = process.env.GATSBY_APP_STORE_LINK ?? "/";
    } else {
      fallbackUrl = process.env.GATSBY_SITE_URL ?? "/";
    }

    window.location.href = appUrl;

    setTimeout(() => {
      window.location.href = fallbackUrl;
    }, 3000);
  }
};

export const clearUserData = (): void => {
  useAccountDetailStore.getState().clear();
  useAuthenticationStore.getState().clear();
  usePaymentStore.getState().clear();
  useConfigurationStore.getState().reset();
  Cookies.remove(CookieType.CURRENCY, {
    domain: process.env.GATSBY_ROOT_DOMAIN,
  });
  Cookies.remove(CookieType.TOKEN, {
    domain: process.env.GATSBY_ROOT_DOMAIN,
  });
  Cookies.remove(CookieType.ACCOUNT_NUMBER, {
    domain: process.env.GATSBY_ROOT_DOMAIN,
  });
  Cookies.remove(CookieType.HASHED_PASSWORD, {
    domain: process.env.GATSBY_ROOT_DOMAIN,
  });
  Cookies.remove(CookieType.AFFILIATE, {
    domain: process.env.GATSBY_ROOT_DOMAIN,
  });
};

export const range = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

type CountryType = Record<string, string>;
export const getRealLocalCountryCode = (): string => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localCountry = (CountriesTimeZone as CountryType)[timezone];
  const localCountryCode = (CountriesIso2 as CountryType)[localCountry];

  return localCountryCode;
};

const countries = (LandingPricing as LandingPricingType).countries;
export const getTranslatedLocationName = (
  value: string,
  language: string,
): string => {
  // Regions
  const region = (Regions as Region[]).find(
    (region: Region) => region.slug === value,
  );
  if (region) {
    return region.name[language] ?? region.name.en ?? "";
  }

  // Countries
  const localizedCountry =
    countries[value.toUpperCase()][language.toLowerCase()] ??
    countries[value.toUpperCase()]?.en;
  return localizedCountry?.country_name ?? "";
};

export const getLocationData = (
  t: TFunction<"translation", undefined, "translation">,
  language: string,
  location: string,
  productId?: string,
):
  | {
      id: string;
      title: string;
      description: string;
      locationText: string;
      imgSrc: string;
      isRegional: boolean;
      coverageList: CoverageItem[];
    }
  | false => {
  const coverageList: CoverageItem[] = [];

  const imgBasePath = "/images/";

  interface LocationDataType {
    id: string;
    locationName: string;
    imgSrc: string;
  }

  const formatRegionId = (region: string) =>
    region
      .replace(/[0-9-]/g, "")
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map(word => word[0]?.toUpperCase() + word.slice(1))
      .join(" ");

  const getCountryData = (id: string = ""): LocationDataType | null => {
    const matchedCountry = Object.keys(countries).find(
      key => key.toLowerCase() === id.toLowerCase(),
    );
    if (!matchedCountry) return null;

    const country =
      countries[matchedCountry.toUpperCase()][language] ||
      countries[matchedCountry.toUpperCase()].en;
    return {
      id: matchedCountry,
      locationName: country?.country_name ?? "",
      imgSrc: `${imgBasePath}countries/${matchedCountry.toLowerCase()}.png`,
    };
  };

  const getRegionData = (slug: string = ""): LocationDataType | null => {
    const matchedRegion = (Regions as Region[]).find(r => r.slug === slug);
    if (!matchedRegion) return null;

    const locationName =
      matchedRegion.name[language] ?? matchedRegion.name.en ?? "";
    const id = matchedRegion.name.en;

    return {
      id,
      locationName,
      imgSrc: `${imgBasePath}regions/${id.replace(" ", "_").toLowerCase()}.png`,
    };
  };

  const buildCoverageList = (coverages: string[] = []): void => {
    coverages.forEach(coverage => {
      const countryName = getTranslatedLocationName(coverage, language);

      coverageList.push({ coverage, country_name: countryName });
    });

    if (coverageList.length > 1) {
      coverageList.sort((a, b) => a.country_name.localeCompare(b.country_name));
    }
  };

  const getMinPriceProduct = (products: Product[]) =>
    products.reduce(
      (min, curr) =>
        parseFloat(curr.price) < parseFloat(min.price) ? curr : min,
      products[0],
    );

  const getSharedData = (data: LocationDataType, products: Product[]) => {
    const product = getMinPriceProduct(products);
    return {
      title: t("og_title_for_plan", {
        location: data.locationName,
        minPriceInEUR: product.price,
      }),
      description: t("og_description_for_plan", {
        location: data.locationName,
        minPriceInEUR: product.price,
      }),
      locationText: data.locationName.replace(/[0-9-]/g, "").trim(),
      imgSrc: data.imgSrc,
    };
  };

  const allProducts = (LandingPricing as LandingPricingType).products;

  if (productId) {
    const product = allProducts.find(p => p.id === productId);
    if (!product) return false;

    const isRegional = product.regional;

    if (isRegional) {
      // Region
      const regionId = formatRegionId(product.region);
      const regionData = getRegionData(
        regionId.replace(/\s+/g, "-").toLowerCase(),
      );
      if (regionData) {
        const products = allProducts.filter(
          p =>
            p.regional &&
            formatRegionId(p.region).toLowerCase() === regionId.toLowerCase(),
        );

        const shared = getSharedData(regionData, products);
        buildCoverageList(product.coverages || []);
        return {
          id: regionData.id,
          isRegional: true,
          coverageList,
          ...shared,
        };
      }
    } else {
      // Country
      const countryId = product.coverages[0]?.toLowerCase();
      const countryData = getCountryData(countryId);
      if (!countryData) return false;

      const products = allProducts.filter(
        p => !p.regional && p.coverages.includes(location.toUpperCase()),
      );

      const shared = getSharedData(countryData, products);
      buildCoverageList(product.coverages || []);
      return {
        id: countryData.id,
        isRegional: false,
        coverageList,
        ...shared,
      };
    }
  }

  // No productId
  const regionData = getRegionData(location);
  if (regionData) {
    // Region
    const products = allProducts.filter(
      p =>
        p.regional &&
        formatRegionId(p.region).toLowerCase() === regionData.id.toLowerCase(),
    );

    const productWithMostCoverages = products.reduce(
      (a, b) => (b.coverages.length > a.coverages.length ? b : a),
      products[0],
    );

    const shared = getSharedData(regionData, products);
    buildCoverageList(productWithMostCoverages?.coverages || []);
    return {
      id: regionData.id,
      isRegional: true,
      coverageList,
      ...shared,
    };
  }

  // Country fallback
  const countryData = getCountryData(location);
  if (!countryData) return false;

  const products = allProducts.filter(
    p => !p.regional && p.coverages.includes(location.toUpperCase()),
  );

  const shared = getSharedData(countryData, products);
  buildCoverageList([location.toUpperCase()]);
  return {
    id: countryData.id,
    isRegional: false,
    coverageList,
    ...shared,
  };
};

export const getPlans = (
  id: string,
  location: string,
  isRegional: boolean,
): {
  lifetimePlans: Product[];
  oneTimePlans: Product[];
  subscriptionPlans: Product[];
  otherLifetimePlans: Product[];
  otherOneTimePlans: Product[];
  otherSubscriptionPlans: Product[];
  regionList: RegionItem[];
  networkCarriers: NetworkCarrier[];
  imsiProviderNames: string[];
  bestNetworksSpeed: string;
} => {
  const lifetimePlans: Product[] = [];
  const oneTimePlans: Product[] = [];
  const subscriptionPlans: Product[] = [];

  const otherLifetimePlans: Product[] = [];
  const otherOneTimePlans: Product[] = [];
  const otherSubscriptionPlans: Product[] = [];

  let regionList: RegionItem[] = []; // for country

  const networkCarriers: NetworkCarrier[] = [];
  const imsiProviderNames: string[] = [];
  let bestNetworksSpeed: string = "";
  if (isRegional) {
    const products = (LandingPricing as LandingPricingType).products.filter(
      (product: Product) =>
        product.regional &&
        product.region
          .replace(/[0-9-]/g, "")
          .trim()
          .toLowerCase() === id.toLowerCase(),
    );

    products?.forEach((product: Product) => {
      switch (product.duration) {
        case -1:
          lifetimePlans.push(product);
          break;
        case 7:
        case 30:
          if (product.has_subscription) {
            subscriptionPlans.push(product);
          } else {
            oneTimePlans.push(product);
          }
          break;
        default:
      }
      const Carriers = product.carriers.flatMap(item =>
        item.split(",").map(str => str.trim()),
      );
      Carriers.forEach((carrier: string, index: number) => {
        if (product.networks[0][index]) {
          const network = product.networks[0][index];

          if (
            !networkCarriers.some(
              item => item.name === carrier && item.logo === network.logo,
            )
          ) {
            networkCarriers.push({
              name: carrier,
              logo: network.logo,
            });
          }
        }
      });
      if (!imsiProviderNames.includes(product.imsi_provider_name)) {
        imsiProviderNames.push(product.imsi_provider_name);
      }
    });
    bestNetworksSpeed = products[0]?.best_networks_speed;
  } else {
    const productRegionalList: string[] = [];
    (LandingPricing as LandingPricingType).products.forEach(
      (product: Product) => {
        if (product.coverages.includes(location.toUpperCase() ?? "")) {
          if (product.regional) {
            productRegionalList.push(
              product.region.replace(/[0-9-]/g, "").trim(),
            );
            switch (product.duration) {
              case -1:
                otherLifetimePlans.push(product);
                break;
              case 7:
              case 30:
                if (product.has_subscription) {
                  otherSubscriptionPlans.push(product);
                } else {
                  otherOneTimePlans.push(product);
                }
                break;
              default:
            }
          } else {
            switch (product.duration) {
              case -1:
                if (product.coverages.length === 1) {
                  lifetimePlans.push(product);
                }

                break;
              case 7:
              case 30:
                if (product.has_subscription) {
                  if (product.coverages.length === 1) {
                    subscriptionPlans.push(product);
                  }
                } else {
                  if (product.coverages.length === 1) {
                    oneTimePlans.push(product);
                  }
                }

                break;
              default:
                break;
            }
            const Carriers = product.carriers.flatMap(item =>
              item.split(",").map(str => str.trim()),
            );
            Carriers.forEach((carrier: string, index: number) => {
              if (product.networks[0][index]) {
                const network = product.networks[0][index];

                if (
                  !networkCarriers.some(
                    item => item.name === carrier && item.logo === network.logo,
                  )
                ) {
                  networkCarriers.push({
                    name: carrier,
                    logo: network.logo,
                  });
                }
              }
            });
            if (!imsiProviderNames.includes(product.imsi_provider_name)) {
              imsiProviderNames.push(product.imsi_provider_name);
            }
          }
        }
      },
    );
    regionList = Array.from(new Set(productRegionalList)).map(region => {
      return {
        link: region.replace(" ", "-").toLowerCase(),
        name: region,
      };
    });
    const firstPlan =
      lifetimePlans[0] ?? subscriptionPlans[0] ?? oneTimePlans[0];
    if (firstPlan) {
      bestNetworksSpeed = firstPlan.best_networks_speed;
    }
  }

  return {
    lifetimePlans,
    oneTimePlans,
    subscriptionPlans,
    otherLifetimePlans,
    otherOneTimePlans,
    otherSubscriptionPlans,
    regionList,
    networkCarriers,
    imsiProviderNames,
    bestNetworksSpeed,
  };
};

export const createPaymentIntent = async (
  product: Product | null,
  currency: string,
  authToken: string,
): Promise<{ paymentIntentId: string; clientSecret: string } | undefined> => {
  if (!product) {
    return;
  }
  const paymentState = usePaymentStore.getState();
  const accountDetailState = useAccountDetailStore.getState();

  const formData = new FormData();
  formData.append("product", product.product);
  formData.append("currency", currency);
  formData.append("bundle_name", process.env.GATSBY_BUNDLE_NAME ?? "");

  if (product.esim) {
    formData.append("action", "esim");
  } else {
    formData.append("action", "sim");
    formData.append("address", "no_address");
    formData.append("shipment", "regular");
    formData.append("method", product.currency);
  }

  if (product.offer_type === "eligible-for-free") {
    formData.append("free_product", "1");
  }

  const url = "/payment_methods/";
  try {
    const response = await axios.post(url, formData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (isDebugMode()) {
      console.log("JSON response:", response.data);
    }
    const paymentIntentId = response.data.transaction.id ?? "";
    const clientSecret = response.data.transaction.client_secret ?? "";
    if (!paymentIntentId || !clientSecret) {
      return;
    }
    paymentState.setPaymentIntentId(paymentIntentId);
    paymentState.setClientSecret(clientSecret);
    accountDetailState.updatePaymentMethods(
      response.data.cards,
      response.data.wallets,
      response.data.coupons,
    );
    return {
      paymentIntentId,
      clientSecret,
    };
  } catch (error: any) {
    if (error.response) {
      console.error(`Response status: ${error.response.status}`);
    } else {
      console.error(error.message);
    }
  }
};

export const preProcessPayment = async (): Promise<void> => {
  const today = moment().format("YYYY-MM-DD");
  const paymentState = usePaymentStore.getState();

  const formData = new FormData();
  formData.append("action", "update-parameters");
  formData.append("piid", paymentState.paymentIntentId);
  formData.append(
    "request_smart_top_up",
    paymentState.isSmartTopUpChecked ? "1" : "0",
  );
  if (paymentState.scheduleDate && paymentState.scheduleDate !== today) {
    formData.append("scheduled_date", paymentState.scheduleDate);
  }
  formData.append("bundle_name", process.env.GATSBY_BUNDLE_NAME ?? "");

  const url = "/payment_methods/";

  const response = await axios.post(url, formData);
  if (isDebugMode()) {
    console.log("JSON response:", response.data);
  }
};

export const getStoreIcons = (
  language: string,
): { appStore: string; playStore: string } => {
  let importedAppStoreIcon: string = "";
  let importedPlayStoreIcon: string = "";
  switch (language) {
    case "de":
    case "es":
    case "fr":
    case "it":
    case "kr":
    case "ru":
    case "zh":
      importedAppStoreIcon = `/images/stores/${language}/app_store_icon.svg`;
      importedPlayStoreIcon = `/images/stores/${language}/play_store_icon.svg`;
      break;
    case "en":
    default:
      importedAppStoreIcon = "/images/stores/en/app_store_icon.svg";
      importedPlayStoreIcon = "/images/stores/en/play_store_icon.svg";
      break;
  }

  return {
    appStore: importedAppStoreIcon,
    playStore: importedPlayStoreIcon,
  };
};
